import { type Membership, queryKeys } from '@hostesa/api-client';
import { getActiveMembership } from '@hostesa/auth-client';

/**
 * Slug resolution: `:tenantSlug/:hotelSlug` (presentation-only) → resolved
 * backend identifiers used in every scoped query key and API path.
 *
 * Master prompt §14 flow:
 *   route params -> resolve slugs -> { tenantId, hotelId, membership }
 *                -> RBAC/ABAC guard -> page queries
 *
 * The backend `GET /api/v1/auth/me` membership list already carries
 * tenant/hotel slugs, ids, and display names, so we can resolve from
 * memberships without a dedicated lookup endpoint.
 *
 * BACKEND GAP: memberships do NOT carry a hotel timezone or per-hotel feature
 * flags. Until a hotel-detail endpoint surfaces those, `timezone` defaults to
 * 'UTC' and feature flags are resolved without per-hotel overrides. Hotel
 * operational timestamps must be re-derived once that endpoint exists.
 */

export interface ResolvedHotelContext {
  readonly tenantId: string;
  readonly hotelId: string;
  readonly tenantSlug: string;
  readonly hotelSlug: string;
  readonly tenantName: string;
  readonly hotelName: string;
  /** IANA timezone for hotel operational timestamps. Defaults to 'UTC' (gap). */
  readonly timezone: string;
  /** The membership that grants access to this tenant/hotel. */
  readonly membership: Membership;
}

/** Reuse the centralised slug-resolution query key for cache parity. */
export const slugResolutionQueryKey = queryKeys.slugResolution;

/**
 * Resolves a tenant/hotel slug pair against the user's memberships.
 *
 * Returns `null` when no membership covers the requested slugs — the caller
 * MUST treat this identically to a tenant-scoped not-found / no-access state
 * and never reveal whether the slug exists in another tenant (master prompt
 * §13, backend 404 semantics).
 *
 * Pure and synchronous: memberships are already in memory from the auth
 * session, so resolution does not require a network round-trip. It is still
 * cached under `['tenant-slug', ...]` (see {@link slugResolutionQueryKey}) so
 * that downstream code reads a single stable resolved context.
 */
export function resolveHotelContext(
  memberships: readonly Membership[],
  tenantSlug: string,
  hotelSlug: string,
): ResolvedHotelContext | null {
  const membership = getActiveMembership(memberships, { tenantSlug, hotelSlug });
  if (!membership) return null;

  // A tenant-wide membership (hotelId: null) grants access to the hotel but
  // does not itself carry the hotel's id/name. We still need a concrete
  // hotelId for scoped query keys and API paths. Prefer a hotel-scoped
  // membership that matches the slug; otherwise we cannot resolve the id from
  // memberships alone and must defer to a hotel-list lookup (gap).
  const hotelScoped = memberships.find(
    (m) => m.tenantSlug === tenantSlug && m.hotelSlug === hotelSlug && m.hotelId !== null,
  );

  const hotelId = hotelScoped?.hotelId ?? membership.hotelId;
  if (!hotelId) {
    // Tenant-wide access without a resolvable hotel id from memberships.
    // BACKEND GAP: needs a hotel-list endpoint keyed by slug to resolve.
    return null;
  }

  return {
    tenantId: membership.tenantId,
    hotelId,
    tenantSlug,
    hotelSlug,
    tenantName: membership.tenantName ?? tenantSlug,
    hotelName: hotelScoped?.hotelName ?? hotelSlug,
    timezone: 'UTC',
    membership: hotelScoped ?? membership,
  };
}

/**
 * Returns the first hotel the user can reach, for post-login redirect and the
 * "no hotel selected" landing. Prefers a hotel-scoped membership (it carries a
 * concrete hotel slug); falls back to a tenant-wide membership's tenant slug.
 */
export function firstAccessibleHotelPath(memberships: readonly Membership[]): string | null {
  const hotelScoped = memberships.find((m) => m.hotelId !== null && m.hotelSlug && m.tenantSlug);
  if (hotelScoped?.tenantSlug && hotelScoped.hotelSlug) {
    return `/${hotelScoped.tenantSlug}/${hotelScoped.hotelSlug}/reception`;
  }
  return null;
}
